import React, { useState, useEffect, useMemo } from 'react';
import { PackagePlus, UserPlus, UserCog, Plus, Trash2, KeyRound, ShieldCheck, AlertTriangle, Copy, Pencil, X, RotateCcw } from "lucide-react";
import { loadAccounts, loadAccountsFromServer, saveAccounts } from "./Login";
import { saveToServer } from '../api';

const ROLE_LABEL = { admin: '관리자', subadmin: '부관리자', staff: '직원', driver: '배달기사' };
const ROLE_COLOR = { admin: '#185FA5', subadmin: '#7C3AED', staff: '#059669', driver: '#D97706' };
const ROLE_BG    = { admin: '#e8f0fb', subadmin: '#f3e8ff', staff: '#e8faf2', driver: '#FEF3C7' };

const EMPTY_ACC = { id: '', name: '', pw: '', role: 'driver' };

const Settings = ({
  currentUser,
  customers = [], setCustomers,
  items = [], setItems,
  specialPrices = [], setSpecialPrices,
  setCylinders, setDeliveries, setCollections, setWarehouses,
}) => {
  // ── 계정 관리 ──
  const [accounts, setAccounts] = useState(loadAccounts); // localStorage 즉시 표시 후 서버 동기화
  useEffect(() => { loadAccountsFromServer().then(setAccounts); }, []);
  const [newAcc, setNewAcc]     = useState(EMPTY_ACC);
  const [accError, setAccError] = useState('');
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw]       = useState('');

  // ── 신규 거래처 ──
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', address: '' });
  const [customerMsg, setCustomerMsg] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);

  // ── 품목 관리 ──
  const [newItem, setNewItem] = useState({ name: '', standardPrice: '' });
  const [itemMsg, setItemMsg] = useState('');

  // ── 특별단가 ──
  const [newSP, setNewSP] = useState({ customerId: '', item: '', price: '' });
  const [spMsg, setSpMsg] = useState('');

  const updateSP = (patch) => { setNewSP(prev => ({ ...prev, ...patch })); setSpMsg(''); };

  // 계정 추가
  const handleAddAccount = () => {
    if (!newAcc.id.trim() || !newAcc.name.trim() || !newAcc.pw.trim()) {
      setAccError('아이디, 이름, 비밀번호를 모두 입력하세요.'); return;
    }
    if (accounts.find(a => a.id === newAcc.id.trim())) {
      setAccError('이미 사용 중인 아이디입니다.'); return;
    }
    const updated = [...accounts, { ...newAcc, id: newAcc.id.trim(), name: newAcc.name.trim() }];
    saveAccounts(updated);
    setAccounts(updated);
    setNewAcc(EMPTY_ACC);
    setAccError('');
  };

  const handleDeleteAccount = (id) => {
    if (id === 'admin') return;
    if (!window.confirm(`'${id}' 계정을 삭제하시겠습니까?`)) return;
    const updated = accounts.filter(a => a.id !== id);
    saveAccounts(updated);
    setAccounts(updated);
  };

  const handleChangePw = (id) => {
    if (!newPw.trim()) return;
    const updated = accounts.map(a => a.id === id ? { ...a, pw: newPw.trim() } : a);
    saveAccounts(updated);
    setAccounts(updated);
    setPwTarget(null);
    setNewPw('');
  };

  // 거래처 삭제
  const handleDeleteCustomer = (id, name) => {
    if (!window.confirm(`'${name}' 거래처를 삭제하시겠습니까?`)) return;
    setCustomers(prev => prev.filter(c => c.id !== id));
  };

  // 거래처 수정 열기 (backward compat: phone → phones)
  const handleOpenEdit = (c) => {
    const phones = c.phones ?? (c.phone ? [c.phone] : []);
    setEditingCustomer({ ...c, phones });
  };

  const handleSaveEdit = () => {
    const { id, name, phones, address } = editingCustomer;
    if (!name.trim()) return;
    const cleanPhones = phones.map(p => p.trim()).filter(Boolean);
    setCustomers(prev => prev.map(c =>
      c.id === id ? { ...c, name: name.trim(), phones: cleanPhones, address: address.trim(), phone: undefined } : c
    ));
    setEditingCustomer(null);
  };

  const editPhone = (i, val) => setEditingCustomer(prev => {
    const phones = [...prev.phones]; phones[i] = val; return { ...prev, phones };
  });
  const addPhone = () => setEditingCustomer(prev => ({ ...prev, phones: [...prev.phones, ''] }));
  const removePhone = (i) => setEditingCustomer(prev => ({ ...prev, phones: prev.phones.filter((_, idx) => idx !== i) }));

  // 거래처 추가
  const handleAddCustomer = () => {
    if (!newCustomer.name.trim()) return;
    if (customers.find(c => c.name === newCustomer.name.trim())) {
      setCustomerMsg(`이미 존재하는 거래처명입니다: '${newCustomer.name.trim()}'`);
      setTimeout(() => setCustomerMsg(''), 3000);
      return;
    }
    const newId = Math.max(0, ...customers.map(c => c.id)) + 1;
    const phones = newCustomer.phone.trim() ? [newCustomer.phone.trim()] : [];
    setCustomers(prev => [...prev, { id: newId, name: newCustomer.name.trim(), phones, address: newCustomer.address.trim() }]);
    setCustomerMsg(`'${newCustomer.name.trim()}' 등록 완료!`);
    setNewCustomer({ name: '', phone: '', address: '' });
    setTimeout(() => setCustomerMsg(''), 2500);
  };

  // 품목 추가
  const handleAddItem = () => {
    if (!newItem.name.trim()) { setItemMsg('품목명을 입력하세요.'); return; }
    if (items.find(i => i.name === newItem.name.trim())) { setItemMsg('이미 존재하는 품목입니다.'); return; }
    const price = parseInt(newItem.standardPrice) || 0;
    const newId = Math.max(0, ...items.map(i => i.id)) + 1;
    setItems(prev => [...prev, { id: newId, name: newItem.name.trim(), unit: '본', standardPrice: price }]);
    setItemMsg(`'${newItem.name.trim()}' 추가 완료!`);
    setNewItem({ name: '', standardPrice: '' });
    setTimeout(() => setItemMsg(''), 2500);
  };

  const handleDeleteItem = (id) => {
    if (!window.confirm('품목을 삭제하시겠습니까?')) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  // 특별단가 추가
  const handleAddSP = () => {
    if (!newSP.customerId) { setSpMsg('거래처를 선택하세요.'); return; }
    if (!newSP.item)       { setSpMsg('품목을 선택하세요.'); return; }
    if (newSP.price === '' || newSP.price === null) { setSpMsg('단가를 입력하세요.'); return; }
    const price = parseInt(newSP.price);
    if (isNaN(price) || price < 0) { setSpMsg('단가는 0 이상의 숫자로 입력하세요.'); return; }
    const customer = customers.find(c => c.id === parseInt(newSP.customerId));
    const exists = specialPrices.find(sp => sp.customerId === parseInt(newSP.customerId) && sp.item === newSP.item);
    if (exists) {
      setSpecialPrices(prev => prev.map(sp =>
        sp.customerId === parseInt(newSP.customerId) && sp.item === newSP.item
          ? { ...sp, price }
          : sp
      ));
      setSpMsg(`✓ '${customer?.name}' ${newSP.item} 단가 수정 완료!`);
    } else {
      setSpecialPrices(prev => [...prev, { customerId: parseInt(newSP.customerId), customerName: customer?.name, item: newSP.item, price }]);
      setSpMsg(`✓ 특별단가 추가 완료!`);
    }
    setNewSP({ customerId: '', item: '', price: '' });
    setTimeout(() => setSpMsg(''), 3000);
  };

  const handleDeleteSP = (customerId, item) => {
    setSpecialPrices(prev => prev.filter(sp => !(sp.customerId === customerId && sp.item === item)));
  };

  // ── 중복 거래처 감지 ──
  const duplicateGroups = useMemo(() => {
    const nameMap = {};
    customers.forEach(c => {
      if (!nameMap[c.name]) nameMap[c.name] = [];
      nameMap[c.name].push(c);
    });
    return Object.values(nameMap).filter(group => group.length > 1);
  }, [customers]);

  // 중복 거래처 병합: 가장 낮은 id를 유지하고 나머지 삭제
  const mergeDuplicates = (group) => {
    const sorted = [...group].sort((a, b) => a.id - b.id);
    const keep = sorted[0];
    const removeIds = sorted.slice(1).map(c => c.id);

    // specialPrices: 삭제될 id의 항목을 keep id로 이전 (중복 품목은 keep 쪽 우선)
    setSpecialPrices(prev => {
      const kept = prev.filter(sp => !removeIds.includes(sp.customerId));
      const toMigrate = prev.filter(sp => removeIds.includes(sp.customerId));
      const merged = [...kept];
      toMigrate.forEach(sp => {
        const alreadyExists = merged.find(m => m.customerId === keep.id && m.item === sp.item);
        if (!alreadyExists) {
          merged.push({ ...sp, customerId: keep.id, customerName: keep.name });
        }
      });
      return merged;
    });

    // 중복 거래처 삭제
    setCustomers(prev => prev.filter(c => !removeIds.includes(c.id)));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>

      {/* ── 계정 관리 (관리자 전용) ── */}
      {currentUser?.role === 'admin' && (
        <div style={sectionBox}>
          <div style={sectionHeader}>
            <ShieldCheck size={20} color="#185FA5" />
            <h3 style={sectionTitle}>계정 관리</h3>
            <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>관리자 전용</span>
          </div>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f8f7f2' }}>
                <th style={thCell}>이름</th>
                <th style={thCell}>아이디</th>
                <th style={thCell}>역할</th>
                <th style={thCell}>비밀번호</th>
                <th style={{ ...thCell, textAlign: 'center' }}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={tdCell}><strong>{a.name}</strong></td>
                  <td style={{ ...tdCell, fontFamily: 'monospace', color: '#555' }}>{a.id}</td>
                  <td style={tdCell}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: ROLE_BG[a.role], color: ROLE_COLOR[a.role] }}>
                      {ROLE_LABEL[a.role]}
                    </span>
                  </td>
                  <td style={tdCell}>
                    {pwTarget === a.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="password" placeholder="새 비밀번호" style={{ ...miniInput, width: 120 }}
                          value={newPw} onChange={e => setNewPw(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleChangePw(a.id)} autoFocus />
                        <button onClick={() => handleChangePw(a.id)} style={smallBtn('#185FA5')}>변경</button>
                        <button onClick={() => { setPwTarget(null); setNewPw(''); }} style={smallBtn('#aaa')}>취소</button>
                      </div>
                    ) : (
                      <button onClick={() => { setPwTarget(a.id); setNewPw(''); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#185FA5', background: 'none', border: '1px solid #185FA533', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                        <KeyRound size={12} /> 변경
                      </button>
                    )}
                  </td>
                  <td style={{ ...tdCell, textAlign: 'center' }}>
                    <button disabled={a.id === 'admin'} onClick={() => handleDeleteAccount(a.id)}
                      style={{ background: 'none', border: 'none', cursor: a.id === 'admin' ? 'default' : 'pointer', opacity: a.id === 'admin' ? 0.25 : 1, padding: 4 }}>
                      <Trash2 size={16} color="#ef4444" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 16, padding: '18px 20px', background: '#f8f7f2', borderRadius: 12, border: '1px solid #e5e3db' }}>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700, color: '#333' }}>새 계정 추가</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
              <div>
                <label style={miniLabel}>이름</label>
                <input style={inputStyle} placeholder="홍길동" value={newAcc.name}
                  onChange={e => { setNewAcc({ ...newAcc, name: e.target.value }); setAccError(''); }} />
              </div>
              <div>
                <label style={miniLabel}>아이디</label>
                <input style={inputStyle} placeholder="user01" value={newAcc.id}
                  onChange={e => { setNewAcc({ ...newAcc, id: e.target.value }); setAccError(''); }} />
              </div>
              <div>
                <label style={miniLabel}>비밀번호</label>
                <input type="password" style={inputStyle} placeholder="초기 비밀번호" value={newAcc.pw}
                  onChange={e => { setNewAcc({ ...newAcc, pw: e.target.value }); setAccError(''); }} />
              </div>
              <div>
                <label style={miniLabel}>역할</label>
                <select style={inputStyle} value={newAcc.role} onChange={e => setNewAcc({ ...newAcc, role: e.target.value })}>
                  <option value="driver">배달기사</option>
                  <option value="subadmin">부관리자</option>
                  <option value="staff">직원</option>
                </select>
              </div>
              <button onClick={handleAddAccount} style={{ ...addBtn, height: 42 }}>
                <Plus size={16} /> 추가
              </button>
            </div>
            {accError && <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#DC2626', fontWeight: 600 }}>{accError}</p>}
          </div>
        </div>
      )}

      {/* ── 중복 거래처 경고 ── */}
      {duplicateGroups.length > 0 && (
        <div style={{ ...sectionBox, border: '1px solid #fbbf24', background: '#fffbeb' }}>
          <div style={sectionHeader}>
            <Copy size={20} color="#D97706" />
            <h3 style={{ ...sectionTitle, color: '#D97706' }}>중복 거래처 감지됨 ({duplicateGroups.length}건)</h3>
          </div>
          <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#92400E' }}>
            동일한 이름의 거래처가 여러 개 등록되어 있습니다. 낮은 ID를 남기고 나머지를 병합합니다. (특별단가 자동 이전)
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {duplicateGroups.map(group => (
              <div key={group[0].name} style={{ background: 'white', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#185FA5' }}>{group[0].name}</span>
                  <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{group.length}개 중복</span>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {group.map((c, i) => (
                      <span key={c.id} style={{ fontSize: 12, background: i === 0 ? '#d1fae5' : '#fee2e2', color: i === 0 ? '#065f46' : '#991b1b', padding: '2px 10px', borderRadius: 12, fontWeight: 600 }}>
                        id={c.id} {i === 0 ? '(유지)' : '(삭제)'} {(c.phones ?? (c.phone ? [c.phone] : [])).length > 0 ? `· ${(c.phones ?? [c.phone]).join(', ')}` : ''} {c.address ? `· ${c.address}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => mergeDuplicates(group)}
                  style={{ padding: '8px 18px', background: '#D97706', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  중복 제거
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 신규 거래처 등록 ── */}
      <div style={sectionBox}>
        <div style={sectionHeader}>
          <UserPlus size={20} color="#EA580C" />
          <h3 style={sectionTitle}>신규 거래처 등록</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr auto', gap: '12px', alignItems: 'flex-end', background: '#fff7ed', padding: '20px', borderRadius: '12px', border: '1px solid #ffedd5' }}>
          <div>
            <label style={miniLabel}>거래처명</label>
            <input style={inputStyle} placeholder="예: 대성식당" value={newCustomer.name}
              onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} />
          </div>
          <div>
            <label style={miniLabel}>연락처</label>
            <input style={inputStyle} placeholder="010-0000-0000" value={newCustomer.phone}
              onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
          </div>
          <div>
            <label style={miniLabel}>주소</label>
            <input style={inputStyle} placeholder="춘천시..." value={newCustomer.address}
              onChange={e => setNewCustomer({ ...newCustomer, address: e.target.value })} />
          </div>
          <button style={{ ...addBtn, background: '#EA580C', height: '42px' }} onClick={handleAddCustomer}>
            <Plus size={18} /> 등록
          </button>
        </div>
        {customerMsg && <p style={{ margin: '10px 0 0 0', fontSize: 13, color: '#059669', fontWeight: 700 }}>✓ {customerMsg}</p>}
        {customers.length > 0 && (
          <table style={{ ...tableStyle, marginTop: 16 }}>
            <thead>
              <tr style={{ background: '#fff7ed' }}>
                <th style={thCell}>거래처명</th>
                <th style={thCell}>연락처</th>
                <th style={thCell}>주소</th>
                <th style={{ ...thCell, textAlign: 'center' }}>수정</th>
                <th style={{ ...thCell, textAlign: 'center' }}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => {
                const phones = c.phones ?? (c.phone ? [c.phone] : []);
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ ...tdCell, fontWeight: 700, color: '#185FA5' }}>{c.name}</td>
                    <td style={tdCell}>
                      {phones.length === 0 ? '—' : phones.map((p, i) => (
                        <div key={i} style={{ fontSize: 13 }}>{p}</div>
                      ))}
                    </td>
                    <td style={{ ...tdCell, color: '#777' }}>{c.address || '—'}</td>
                    <td style={{ ...tdCell, textAlign: 'center' }}>
                      <button onClick={() => handleOpenEdit(c)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5 }}>
                        <Pencil size={15} color="#185FA5" />
                      </button>
                    </td>
                    <td style={{ ...tdCell, textAlign: 'center' }}>
                      <button onClick={() => handleDeleteCustomer(c.id, c.name)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5 }}>
                        <Trash2 size={16} color="#ef4444" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 품목 관리 ── */}
      <div style={sectionBox}>
        <div style={sectionHeader}>
          <PackagePlus size={20} color="#185FA5" />
          <h3 style={sectionTitle}>전체 취급 품목 관리</h3>
        </div>
        <div style={addBar}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>품목명</label>
            <input style={inputStyle} placeholder="예: 아르곤" value={newItem.name}
              onChange={e => { setNewItem({ ...newItem, name: e.target.value }); setItemMsg(''); }} />
          </div>
          <div style={{ width: 140 }}>
            <label style={miniLabel}>표준단가 (원)</label>
            <input style={inputStyle} placeholder="예: 55000" type="number" min="0" value={newItem.standardPrice}
              onChange={e => { setNewItem({ ...newItem, standardPrice: e.target.value }); setItemMsg(''); }} />
          </div>
          <button style={{ ...addBtn, alignSelf: 'flex-end' }} onClick={handleAddItem}>
            <Plus size={16} /> 품목 추가
          </button>
        </div>
        {itemMsg && <p style={{ margin: '-10px 0 12px 0', fontSize: 13, color: itemMsg.includes('완료') ? '#059669' : '#DC2626', fontWeight: 700 }}>{itemMsg.includes('완료') ? '✓ ' : ''}{itemMsg}</p>}
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={thCell}>품목명</th>
              <th style={thCell}>표준 판매단가</th>
              <th style={{ ...thCell, textAlign: 'center' }}>삭제</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdCell}>{item.name}</td>
                <td style={tdCell}>{item.standardPrice.toLocaleString()}원</td>
                <td style={{ ...tdCell, textAlign: 'center' }}>
                  <button onClick={() => handleDeleteItem(item.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5 }}>
                    <Trash2 size={16} color="#ef4444" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 거래처별 특별 단가 ── */}
      <div style={sectionBox}>
        <div style={sectionHeader}>
          <UserCog size={20} color="#059669" />
          <h3 style={sectionTitle}>거래처별 특별 단가 설정</h3>
        </div>
        <div style={{ ...addBar, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', minWidth: 140 }}>
            <label style={miniLabel}>거래처</label>
            <select style={inputStyle} value={newSP.customerId}
              onChange={e => updateSP({ customerId: e.target.value })}>
              <option value="">거래처 선택</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 140 }}>
            <label style={miniLabel}>품목</label>
            <select style={inputStyle} value={newSP.item}
              onChange={e => updateSP({ item: e.target.value })}>
              <option value="">품목 선택</option>
              {items.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={miniLabel}>특별단가 (원)</label>
            <input style={inputStyle} placeholder="예: 40000" type="number" min="0" value={newSP.price}
              onChange={e => updateSP({ price: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && handleAddSP()} />
          </div>
          <button style={{ ...addBtn, background: '#059669', alignSelf: 'flex-end' }} onClick={handleAddSP}>
            <Plus size={16} /> 설정 추가
          </button>
        </div>
        {spMsg && (
          <p style={{ margin: '-10px 0 12px 0', fontSize: 13, fontWeight: 700,
            color: spMsg.startsWith('✓') ? '#059669' : '#DC2626' }}>
            {spMsg}
          </p>
        )}
        {specialPrices.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f0fdf4' }}>
                <th style={thCell}>거래처명</th>
                <th style={thCell}>품목</th>
                <th style={thCell}>특별단가</th>
                <th style={{ ...thCell, textAlign: 'center' }}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {specialPrices.map((sp, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ ...tdCell, fontWeight: 600, color: '#185FA5' }}>{sp.customerName}</td>
                  <td style={tdCell}>{sp.item}</td>
                  <td style={tdCell}>{sp.price.toLocaleString()}원</td>
                  <td style={{ ...tdCell, textAlign: 'center' }}>
                    <button onClick={() => handleDeleteSP(sp.customerId, sp.item)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5 }}>
                      <Trash2 size={16} color="#ef4444" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 서버 백업 복원 (관리자 전용) ── */}
      {currentUser?.role === 'admin' && (
        <ServerBackupRestore
          setDeliveries={setDeliveries}
          setCollections={setCollections}
          setCustomers={setCustomers}
          setCylinders={setCylinders}
        />
      )}

      {/* ── 데이터 초기화 (관리자 전용) ── */}
      {currentUser?.role === 'admin' && (
        <div style={{ ...sectionBox, border: '1px solid #fecaca' }}>
          <div style={sectionHeader}>
            <AlertTriangle size={20} color="#DC2626" />
            <h3 style={{ ...sectionTitle, color: '#DC2626' }}>데이터 초기화</h3>
            <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>관리자 전용 · 되돌릴 수 없습니다</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <ResetBtn label="거래처 전체 삭제" onConfirm={async () => { await saveToServer('customers', [], { force: true }); setCustomers([]); }} />
            <ResetBtn label="용기/재고 전체 삭제" onConfirm={async () => { await saveToServer('cylinders', [], { force: true }); setCylinders([]); }} />
            <ResetBtn label="배송 이력 삭제" onConfirm={async () => { await saveToServer('deliveries', [], { force: true }); setDeliveries([]); }} />
            <ResetBtn label="매입 이력 삭제" onConfirm={async () => { await saveToServer('collections', [], { force: true }); setCollections([]); }} />
            <ResetBtn label="창고 목록 삭제" onConfirm={async () => { await saveToServer('warehouses', [], { force: true }); setWarehouses([]); }} />
            <ResetBtn label="품목 전체 삭제" onConfirm={async () => { await saveToServer('items', [], { force: true }); setItems([]); }} />
            <ResetBtn
              label="⚠ 전체 데이터 초기화"
              danger
              onConfirm={async () => {
                await Promise.all([
                  saveToServer('customers',     [], { force: true }),
                  saveToServer('cylinders',     [], { force: true }),
                  saveToServer('deliveries',    [], { force: true }),
                  saveToServer('collections',   [], { force: true }),
                  saveToServer('warehouses',    [], { force: true }),
                  saveToServer('items',         [], { force: true }),
                  saveToServer('specialPrices', [], { force: true }),
                ]);
                setCustomers([]);
                setCylinders([]);
                setDeliveries([]);
                setCollections([]);
                setWarehouses([]);
                setItems([]);
                setSpecialPrices([]);
              }}
            />
          </div>
        </div>
      )}

      {/* ── 거래처 수정 모달 ── */}
      {editingCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setEditingCustomer(null); }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>거래처 수정</h3>
              <button onClick={() => setEditingCustomer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} color="#999" />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={miniLabel}>거래처명</label>
                <input style={inputStyle} value={editingCustomer.name}
                  onChange={e => setEditingCustomer(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label style={miniLabel}>주소</label>
                <input style={inputStyle} value={editingCustomer.address}
                  onChange={e => setEditingCustomer(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div>
                <label style={miniLabel}>연락처</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {editingCustomer.phones.map((ph, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input style={{ ...inputStyle, flex: 1 }} value={ph} placeholder="010-0000-0000"
                        onChange={e => editPhone(i, e.target.value)} />
                      <button onClick={() => removePhone(i)}
                        style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', padding: '8px 10px', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button onClick={addPhone}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#185FA5', background: '#e8f0fb', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start' }}>
                    <Plus size={14} /> 연락처 추가
                  </button>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingCustomer(null)}
                style={{ padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                취소
              </button>
              <button onClick={handleSaveEdit}
                style={{ padding: '10px 24px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const ResetBtn = ({ label, onConfirm, danger = false }) => {
  const [saving, setSaving] = useState(false);
  const handleClick = async () => {
    const msg = danger
      ? `정말로 모든 데이터를 초기화하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`
      : `'${label}'를 실행하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    await onConfirm();
    setSaving(false);
  };
  return (
    <button onClick={handleClick} disabled={saving} style={{
      padding: '9px 16px', border: `1px solid ${danger ? '#DC2626' : '#fca5a5'}`,
      background: saving ? '#aaa' : danger ? '#DC2626' : '#fff5f5',
      color: danger || saving ? '#fff' : '#DC2626',
      borderRadius: 8, fontSize: 13, fontWeight: 700,
      cursor: saving ? 'not-allowed' : 'pointer',
    }}>
      {saving ? '저장 중...' : label}
    </button>
  );
};

// ── 서버 백업 복원 컴포넌트 ──────────────────────────────────
const KEY_LABELS = {
  deliveries: '배송(판매) 이력',
  collections: '매입 이력',
  customers: '거래처',
  cylinders: '용기/재고',
  orders: '주문',
};

const ServerBackupRestore = ({ setDeliveries, setCollections, setCustomers, setCylinders }) => {
  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/backups');
      setBackups(await res.json());
    } catch { setMsg('백업 목록 조회 실패'); }
    setLoading(false);
  };

  const restore = async (key, date) => {
    if (!window.confirm(`${KEY_LABELS[key] || key}을(를) ${date} 백업으로 복원하시겠습니까?\n현재 데이터가 덮어씌워집니다.`)) return;
    try {
      const res  = await fetch(`/api/restore/${key}/${date}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { setMsg(`복원 실패: ${data.error}`); return; }
      // 앱 상태 동기화
      const fresh = await fetch('/api/data');
      const all   = await fresh.json();
      if (key === 'deliveries'  && all.deliveries)  setDeliveries(all.deliveries);
      if (key === 'collections' && all.collections) setCollections(all.collections);
      if (key === 'customers'   && all.customers)   setCustomers(all.customers);
      if (key === 'cylinders'   && all.cylinders)   setCylinders(all.cylinders);
      setMsg(`✅ ${KEY_LABELS[key] || key} 복원 완료 (${data.count}건)`);
      fetchBackups();
    } catch (e) { setMsg(`복원 오류: ${e.message}`); }
    setTimeout(() => setMsg(''), 5000);
  };

  const targetKeys = Object.keys(KEY_LABELS);

  return (
    <div style={{ ...sectionBox, border: '1px solid #dbeafe' }}>
      <div style={sectionHeader}>
        <RotateCcw size={20} color="#185FA5" />
        <h3 style={sectionTitle}>서버 자동 백업에서 복원</h3>
        <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>관리자 전용 · 데이터가 사라졌을 때 복원</span>
      </div>
      <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#555' }}>
        서버는 매일 자동으로 데이터를 백업합니다. 데이터가 사라진 경우 아래에서 날짜를 선택하여 복원하세요.
      </p>
      {!backups ? (
        <button onClick={fetchBackups} disabled={loading}
          style={{ padding: '9px 20px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          {loading ? '조회 중...' : '백업 목록 조회'}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {targetKeys.filter(k => backups[k]?.length > 0).map(key => (
            <div key={key} style={{ background: '#f0f6ff', borderRadius: 10, padding: '14px 16px', border: '1px solid #bfdbfe' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#185FA5', marginBottom: 10 }}>{KEY_LABELS[key]}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(backups[key] || []).slice(0, 14).map(date => (
                  <button key={date} onClick={() => restore(key, date)}
                    style={{ padding: '6px 14px', background: 'white', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#1d4ed8', cursor: 'pointer' }}>
                    {date}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {targetKeys.every(k => !backups[k]?.length) && (
            <p style={{ fontSize: 13, color: '#888' }}>저장된 백업이 없습니다. (오늘 이후 자동 생성됩니다)</p>
          )}
          <button onClick={fetchBackups} style={{ alignSelf: 'flex-start', padding: '6px 14px', background: '#f0f0f0', border: 'none', borderRadius: 7, fontSize: 12, color: '#555', cursor: 'pointer', fontWeight: 600 }}>
            새로고침
          </button>
        </div>
      )}
      {msg && (
        <p style={{ margin: '12px 0 0 0', fontSize: 13, fontWeight: 700, color: msg.startsWith('✅') ? '#059669' : '#DC2626' }}>{msg}</p>
      )}
    </div>
  );
};

const smallBtn = (bg) => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' });

const sectionBox    = { background: 'white', padding: '25px', borderRadius: '16px', border: '1px solid #d3d1c7' };
const sectionHeader = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: '12px' };
const sectionTitle  = { margin: 0, fontSize: '17px', fontWeight: 700 };
const miniLabel     = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '6px' };
const addBar        = { display: 'flex', gap: '10px', marginBottom: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', alignItems: 'flex-start' };
const inputStyle    = { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const addBtn        = { padding: '10px 20px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' };
const tableStyle    = { width: '100%', borderCollapse: 'collapse' };
const thCell        = { padding: '11px 14px', textAlign: 'left', fontSize: 13, color: '#777', fontWeight: 600, borderBottom: '2px solid #eee' };
const tdCell        = { padding: '11px 14px', fontSize: 14 };
const miniInput     = { padding: '6px 10px', border: '1px solid #d3d1c7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default Settings;
